import { createFileRoute, Link } from "@tanstack/react-router";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { redirectIfAuthenticatedClient } from "@/lib/services/auth-guard";

export const Route = createFileRoute("/forgot-password")({
  beforeLoad: async () => {
    await redirectIfAuthenticatedClient();
  },
  component: ForgotPasswordPage,
  head: () => ({
    meta: [
      { title: "Recuperar contraseña — Structura Market" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
});

function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [sent, setSent]   = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy]   = useState(false);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      const res = await fetch("/api/auth/forgot-password", {
        method:  "POST",
        headers: { "Content-Type": "application/json" },
        body:    JSON.stringify({ email: email.trim().toLowerCase() }),
      });
      if (!res.ok) {
        const data = (await res.json()) as { error?: string };
        setError(data.error ?? "Ocurrió un error. Intentá de nuevo.");
        return;
      }
      setSent(true);
    } catch {
      setError("Error de red. Verificá tu conexión e intentá de nuevo.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="w-full max-w-md rounded-2xl border border-border/50 bg-card p-8 shadow-sm">
        <h1 className="text-xl font-bold text-foreground">Recuperar contraseña</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Ingresá tu email y te enviamos un enlace para crear una contraseña nueva.
        </p>

        {sent ? (
          <p className="mt-6 rounded-lg bg-green-50 border border-green-200 p-4 text-sm text-green-700 dark:bg-green-950/20 dark:border-green-900/40 dark:text-green-400">
            Si el email está registrado, vas a recibir las instrucciones en unos minutos. Revisá
            también tu carpeta de spam.
          </p>
        ) : (
          <form className="mt-6 space-y-4" onSubmit={(e) => void onSubmit(e)}>
            <div>
              <Label className="text-xs">Email</Label>
              <Input
                type="email"
                className="mt-1"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="email"
                required
              />
            </div>
            {error && <p className="text-xs text-destructive">{error}</p>}
            <Button type="submit" className="w-full" disabled={busy}>
              {busy ? "Enviando…" : "Enviar instrucciones"}
            </Button>
          </form>
        )}

        <p className="mt-6 text-center text-sm text-muted-foreground">
          <Link
            to="/acceso/comercio"
            search={{ returnTo: undefined, error: undefined }}
            className="text-primary underline-offset-4 hover:underline"
          >
            Volver al login
          </Link>
        </p>
      </div>
    </div>
  );
}
